<template>
  <!-- Decorative field of nested brackets — ([{< >}]) — after the banner on
       Supabase's event pages. Pairs slowly swap type, so the field drifts;
       every ten seconds or so a wave washes over it in the next palette
       colour, openers first and then the closers that match them.

       Each row is one nest around a pivot column: openers on one side, their
       closers mirrored on the other, so what's on screen always balances.
       `invert` swaps the sides (closers, then openers) for a field on the
       right-hand edge.

       Pure ornament: aria-hidden, no pointer events, and drawn on one canvas
       rather than hundreds of spans. Nothing renders on the server — it fades
       in after the page is idle. It stops while off screen, and paints one
       still frame for prefers-reduced-motion.

       Positioned by the parent's classes; the canvas fills this box. -->
  <div
    class="pointer-events-none transition-opacity duration-1000 motion-reduce:transition-none"
    :class="[`bracket-field--fade-${fade}`, ready ? 'opacity-100' : 'opacity-0']"
    aria-hidden="true"
  >
    <canvas ref="canvas" class="block h-full w-full" />
  </div>
</template>

<script>
// One beat for every field on the page, so a pair of them (the ribbon's two
// sides) wave together and in the same colour instead of drifting apart. The
// first field to notice the beat is due moves it on; each field then starts
// its own wave when it sees the count change.
const beat = { at: Infinity, count: Math.floor(Math.random() * 4) }
</script>

<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'

const props = defineProps({
  // Glyph size in CSS px. Rows sit `lineHeight` × this apart; columns are the
  // font's advance plus `tracking` (both in em).
  fontSize: { type: Number, default: 13 },
  lineHeight: { type: Number, default: 1.5 },
  tracking: { type: Number, default: 0.12 },
  // A fixed row count stretches the row pitch to fill the height exactly.
  // 0 = as many rows as fit, the last one cut by the bottom edge.
  rows: { type: Number, default: 0 },
  // The edge the columns are flush with: 'left' | 'right'.
  anchor: { type: String, default: 'left' },
  invert: { type: Boolean, default: false },
  // Where the rows turn around, as a fraction of the width, ± a per-row jitter.
  pivot: { type: Number, default: 0.5 },
  pivotJitter: { type: Number, default: 0.15 },
  // Which edge the field fades out toward: 'none' | 'left' | 'right' | 'down'.
  fade: { type: String, default: 'none' },
  // Resting glyphs: an RGB triple and its opacity.
  ink: { type: Array, default: () => [165, 180, 252] },
  inkAlpha: { type: Number, default: 0.1 },
  // Opacity of a glyph at the crest of a wave.
  peakAlpha: { type: Number, default: 0.5 },
  // Each wave takes the next colour, so the field cycles through a palette.
  palette: { type: Array, default: () => ['#a78bfa', '#38bdf8', '#f472b6', '#fcd34d'] },
  // Milliseconds between waves (±20%). The beat is shared page-wide, so
  // whichever field moves it on sets the next interval.
  every: { type: Number, default: 11000 },
  // Brackets near the mouse light up. Listens on the parent element, since
  // this layer takes no pointer events of its own.
  interactive: { type: Boolean, default: false },
})

// Glyph codes 0–3 open, 4–7 close; a code and code + 4 are a matching pair.
const GLYPHS = '([{<)]}>'
const MONO = 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, "Liberation Mono", monospace'
const HOT = [238, 242, 255]

// Everything here is slow on purpose: this sits behind copy, and it should
// read as texture that happens to move, not as something to watch.
const FRAME_MS = 50 // 20fps while a wave runs or the mouse is over it; nothing moves fast enough to need more
const IDLE_FRAME_MS = 125 // 8fps otherwise, when only the slow swaps and fades are moving
const TICK_MS = 240 // how often each row may swap a pair
const SWAP_CHANCE = 0.35 // …and how likely it is to
const DECAY_MS = 1800 // time constant of a lit glyph's fade
const HALF_WAVE_MS = 2200 // one side of a row: all its openers, or all its closers
const STAGGER_MS = 160 // per-row delay, so a wave runs on a diagonal…
const STAGGER_TOTAL_MS = 1800 // …capped, so a tall field isn't swept for ages
const GAP_MS = 500 // between a row's openers and its closers
const HOLD_MS = 3000 // a lit row stops swapping for this long, so it reads while it glows
const CENTRE_GLOW = 0.25 // the innermost pair of every row never goes fully dark
const TWINKLE = 0.3 // a swapped pair sometimes glints this bright
const POINTER_GLOW = 0.5
const POINTER_REACH = 3.2 // spotlight radius, in columns

const canvas = ref(null)
const ready = ref(false)

let ctx = null
// Copied out of `props` once: the per-cell loops run tens of thousands of
// times a second, and every read of a prop goes through Vue's reactive proxy.
let invert = false
// Energy steps pre-mixed per palette colour, so a frame never builds a colour
// string. A faint field needs fewer: every step is a repaint as a glyph fades.
let levels = 20
let width = 0
let height = 0
let cols = 0
let rowCount = 0
let cellW = 0
let rowH = 0
let offX = 0
let stagger = STAGGER_MS
let pivotMin = 1
let pivotMax = 1

let types = [] // per row: the bracket type (0–3) at each depth from the pivot
let pivots = [] // per row: the first column past the turn
let lockedUntil = []
let energy = new Float32Array(0)
let hue = new Uint8Array(0)
let drawn = new Int32Array(0) // what each cell was last painted as, so unchanged cells are skipped

let colours = []
let ambient = 0 // palette index of the latest wave; twinkles and the centre pairs use it too
let wave = null
let seenBeat = 0
let nextTickAt = 0
let pointer = null

let raf = 0
let timer = 0
let running = false
let visible = false
let reduced = false
let lastFrame = 0
let idleHandle = null
let resizeObserver = null
let intersectionObserver = null
let motionQuery = null
let host = null

const random = (n) => Math.floor(Math.random() * n)
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v))

function hexToRgb(hex) {
  const n = parseInt(hex.slice(1), 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

// Energy 0 is the resting ink. Energy tints it toward the palette colour
// (fully there by 0.7, then a little paler at the very crest) and raises its
// opacity from `inkAlpha` to `peakAlpha`.
function buildColours() {
  const ink = props.ink
  const a0 = props.inkAlpha
  const a1 = props.peakAlpha
  levels = clamp(Math.round((a1 - a0) * 50), 6, 20)
  colours = props.palette.map((hex) => {
    const tint = hexToRgb(hex)
    const steps = []
    for (let q = 0; q < levels; q++) {
      const e = q / (levels - 1)
      const toTint = Math.min(1, e / 0.7)
      const toHot = Math.max(0, (e - 0.7) / 0.3) * 0.35
      const [r, g, b] = [0, 1, 2].map((k) => {
        const c = ink[k] + (tint[k] - ink[k]) * toTint
        return Math.round(c + (HOT[k] - c) * toHot)
      })
      steps.push(`rgba(${r},${g},${b},${(a0 + (a1 - a0) * e).toFixed(3)})`)
    }
    return steps
  })
}

function randomType(not = -1) {
  const t = random(4)
  return t === not ? (t + 1 + random(3)) % 4 : t
}

function makeRow(r) {
  // Deep enough for the far side of the widest possible turn.
  const depth = new Uint8Array(cols + 2)
  for (let d = 0; d < depth.length; d++) depth[d] = randomType()
  types[r] = depth
  pivots[r] = pivotMin + random(pivotMax - pivotMin + 1)
  lockedUntil[r] = 0
}

// Column of the glyph `d` steps out from row r's turn, on each side.
const openCol = (r, d) => (invert ? pivots[r] + d : pivots[r] - 1 - d)
const closeCol = (r, d) => (invert ? pivots[r] - 1 - d : pivots[r] + d)
const openCount = (r) => (invert ? cols - pivots[r] : pivots[r])

function glyphAt(r, c) {
  const p = pivots[r]
  const before = c < p
  const type = types[r][before ? p - 1 - c : c - p]
  return before !== invert ? type : type + 4
}

function light(r, c, e, h) {
  if (r < 0 || r >= rowCount || c < 0 || c >= cols) return
  const i = r * cols + c
  if (energy[i] < e) {
    energy[i] = e
    hue[i] = h
  }
}

function lightPair(r, d, e, h) {
  light(r, openCol(r, d), e, h)
  light(r, closeCol(r, d), e, h)
}

// Sizes the canvas to its box and rebuilds the grid. False while the box is
// empty (display: none, or not laid out yet).
function layout() {
  const el = canvas.value
  const w = el.clientWidth
  const h = el.clientHeight
  if (!w || !h) return false

  width = w
  height = h
  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  el.width = Math.round(w * dpr)
  el.height = Math.round(h * dpr)
  ctx = el.getContext('2d')
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.font = `${props.fontSize}px ${MONO}`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'

  cellW = ctx.measureText('[').width + props.tracking * props.fontSize
  rowH = props.rows ? h / props.rows : props.fontSize * props.lineHeight
  rowCount = props.rows || Math.ceil(h / rowH)
  cols = Math.ceil(w / cellW)
  offX = props.anchor === 'right' ? w - cols * cellW : 0
  stagger = Math.min(STAGGER_MS, STAGGER_TOTAL_MS / rowCount)
  pivotMin = clamp(Math.round(cols * (props.pivot - props.pivotJitter)), 1, cols - 1)
  pivotMax = clamp(Math.round(cols * (props.pivot + props.pivotJitter)), pivotMin, cols - 1)

  types = []
  pivots = []
  lockedUntil = []
  for (let r = 0; r < rowCount; r++) makeRow(r)
  energy = new Float32Array(rowCount * cols)
  hue = new Uint8Array(rowCount * cols)
  drawn = new Int32Array(rowCount * cols).fill(-1)
  wave = null
  return true
}

// Rows swap one pair at a time, mostly unlit, so the field drifts without
// competing with the waves. Once in a long while a row's turn moves a column.
function shuffle(now) {
  for (let r = 0; r < rowCount; r++) {
    if (now < lockedUntil[r]) continue
    if (Math.random() < SWAP_CHANCE) {
      const d = random(Math.max(pivots[r], cols - pivots[r]))
      types[r][d] = randomType(types[r][d])
      if (Math.random() < 0.2) lightPair(r, d, TWINKLE, ambient)
    }
    if (Math.random() < 0.004) {
      pivots[r] = clamp(pivots[r] + (Math.random() < 0.5 ? -1 : 1), pivotMin, pivotMax)
    }
  }
}

function startWave(now) {
  ambient = beat.count % colours.length
  wave = { t0: now, hue: ambient, opened: new Int16Array(rowCount), closed: new Int16Array(rowCount) }
}

// A row's openers light from the outside in, then — once they've all
// arrived at the turn — its closers from the turn out: open, then close.
function stepWave(now) {
  let done = true
  for (let r = 0; r < rowCount; r++) {
    const t = now - wave.t0 - r * stagger
    const opens = openCount(r)
    const closes = cols - opens
    if (t < 0) {
      done = false
      continue
    }
    lockedUntil[r] = now + HOLD_MS

    const openTo = Math.min(opens, Math.floor((t / HALF_WAVE_MS) * opens) + 1)
    while (wave.opened[r] < openTo) {
      light(r, openCol(r, opens - 1 - wave.opened[r]), 1, wave.hue)
      wave.opened[r]++
    }
    const tc = t - HALF_WAVE_MS - GAP_MS
    if (tc >= 0) {
      const closeTo = Math.min(closes, Math.floor((tc / HALF_WAVE_MS) * closes) + 1)
      while (wave.closed[r] < closeTo) {
        light(r, closeCol(r, wave.closed[r]), 1, wave.hue)
        wave.closed[r]++
      }
    }
    if (wave.opened[r] < opens || wave.closed[r] < closes) done = false
  }
  if (done) wave = null
}

function glowCentres() {
  for (let r = 0; r < rowCount; r++) lightPair(r, 0, CENTRE_GLOW, ambient)
}

function spotlight() {
  const cc = (pointer.x - offX) / cellW
  const rr = pointer.y / rowH
  // Rows are taller than columns are wide; measure distance in column widths.
  const squash = rowH / cellW
  const rowReach = Math.ceil(POINTER_REACH / squash)
  for (let r = Math.floor(rr) - rowReach; r <= Math.floor(rr) + rowReach; r++) {
    for (let c = Math.floor(cc - POINTER_REACH); c <= Math.ceil(cc + POINTER_REACH); c++) {
      const dist = Math.hypot(c + 0.5 - cc, (r + 0.5 - rr) * squash) / POINTER_REACH
      if (dist < 1) light(r, c, POINTER_GLOW * (1 - dist) ** 1.5, ambient)
    }
  }
}

// Repaints only the cells whose glyph or colour step changed since the last
// frame; a resting field costs next to nothing.
function paint(all = false) {
  for (let r = 0; r < rowCount; r++) {
    const y = r * rowH
    for (let c = 0; c < cols; c++) {
      const i = r * cols + c
      const glyph = glyphAt(r, c)
      const q = Math.round(energy[i] * (levels - 1))
      const h = q ? hue[i] : 0
      const key = (glyph << 16) | (h << 8) | q
      if (!all && drawn[i] === key) continue
      drawn[i] = key
      const x = offX + c * cellW
      ctx.clearRect(x, y, cellW, rowH)
      ctx.fillStyle = colours[h][q]
      ctx.fillText(GLYPHS[glyph], x + cellW / 2, y + rowH / 2)
    }
  }
}

function paintStill() {
  energy.fill(0)
  glowCentres()
  ctx.clearRect(0, 0, width, height)
  paint(true)
}

// A timer, then one animation frame. Asking for a frame on every vsync and
// skipping most of them would still run the page's rendering steps each time.
function schedule() {
  timer = setTimeout(() => {
    raf = requestAnimationFrame(frame)
  }, wave || pointer ? FRAME_MS : IDLE_FRAME_MS)
}

function frame(now) {
  const fade = Math.exp(-Math.min(now - lastFrame, 250) / DECAY_MS)
  lastFrame = now

  for (let i = 0; i < energy.length; i++) energy[i] *= fade
  if (now >= nextTickAt) {
    shuffle(now)
    nextTickAt = now + TICK_MS
  }
  if (now >= beat.at) {
    beat.count++
    beat.at = now + props.every * (0.8 + Math.random() * 0.4)
  }
  // A beat that lands mid-wave is skipped rather than queued behind it.
  if (seenBeat !== beat.count) {
    seenBeat = beat.count
    if (!wave) startWave(now)
  }
  if (wave) stepWave(now)
  glowCentres()
  if (pointer) spotlight()
  paint()
  if (running) schedule()
}

function start() {
  if (running || reduced || !visible || !ctx) return
  running = true
  lastFrame = performance.now()
  nextTickAt = lastFrame
  // Coming into view is the moment worth marking: the next wave follows
  // shortly after, rather than up to a full interval later.
  beat.at = Math.min(beat.at, lastFrame + 1200)
  raf = requestAnimationFrame(frame)
}

function stop() {
  running = false
  clearTimeout(timer)
  cancelAnimationFrame(raf)
}

function onResize() {
  const el = canvas.value
  if (!el || (el.clientWidth === width && el.clientHeight === height)) return
  stop()
  if (!layout()) return
  paintStill()
  start()
}

function onMotionChange() {
  reduced = motionQuery.matches
  if (!ctx) return
  if (reduced) {
    stop()
    paintStill()
  } else {
    start()
  }
}

function onPointerMove(event) {
  if (event.pointerType !== 'mouse' || !canvas.value) return
  const rect = canvas.value.getBoundingClientRect()
  pointer = { x: event.clientX - rect.left, y: event.clientY - rect.top }
}

function onPointerLeave() {
  pointer = null
}

function boot() {
  idleHandle = null
  const el = canvas.value
  if (!el) return
  invert = props.invert
  buildColours()
  // Join the beat where it is: no replaying a wave the other fields already ran.
  seenBeat = beat.count
  ambient = beat.count % colours.length

  resizeObserver = new ResizeObserver(onResize)
  resizeObserver.observe(el)
  intersectionObserver = new IntersectionObserver(([entry]) => {
    visible = entry.isIntersecting
    if (visible) start()
    else stop()
  })
  intersectionObserver.observe(el)

  if (props.interactive && el.parentElement?.parentElement) {
    host = el.parentElement.parentElement
    host.addEventListener('pointermove', onPointerMove, { passive: true })
    host.addEventListener('pointerleave', onPointerLeave)
  }

  if (layout()) paintStill()
  ready.value = true
  start()
}

onMounted(() => {
  motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
  reduced = motionQuery.matches
  motionQuery.addEventListener?.('change', onMotionChange)
  // Decoration waits its turn: first paint and hydration come first.
  idleHandle = 'requestIdleCallback' in window
    ? { idle: requestIdleCallback(boot, { timeout: 1500 }) }
    : { timeout: setTimeout(boot, 200) }
})

onBeforeUnmount(() => {
  stop()
  if (idleHandle?.idle) cancelIdleCallback(idleHandle.idle)
  if (idleHandle?.timeout) clearTimeout(idleHandle.timeout)
  resizeObserver?.disconnect()
  intersectionObserver?.disconnect()
  motionQuery?.removeEventListener?.('change', onMotionChange)
  host?.removeEventListener('pointermove', onPointerMove)
  host?.removeEventListener('pointerleave', onPointerLeave)
})
</script>

<style scoped>
/* Full strength only at the far edge, gone well before the other side. */
.bracket-field--fade-right {
  -webkit-mask-image: linear-gradient(to right, #000, rgb(0 0 0 / 0.4) 45%, transparent 85%);
  mask-image: linear-gradient(to right, #000, rgb(0 0 0 / 0.4) 45%, transparent 85%);
}
.bracket-field--fade-left {
  -webkit-mask-image: linear-gradient(to left, #000, rgb(0 0 0 / 0.4) 45%, transparent 85%);
  mask-image: linear-gradient(to left, #000, rgb(0 0 0 / 0.4) 45%, transparent 85%);
}
.bracket-field--fade-down {
  -webkit-mask-image: linear-gradient(to bottom, #000, transparent);
  mask-image: linear-gradient(to bottom, #000, transparent);
}
</style>
